import React, { useState, useEffect, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Box,
  Typography,
  Chip,
  Grid,
  InputAdornment,
  CircularProgress,
  Tooltip,
  FormControlLabel,
  Switch,
  alpha,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { getAllItems } from "../../../Services/Services";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import InfiniteScroll from "react-infinite-scroll-component";

function MinimumStockAlertReport() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [onlyBelowMin, setOnlyBelowMin] = useState(true);
  const [filters, setFilters] = useState({
    item_code: "",
    name: "",
    category: "",
    sub_category: "",
  });

  const fetchItems = async (targetPage = 1, append = false) => {
    try {
      if (!append) setLoading(true);
      const data = await getAllItems(targetPage, filters);
      const results = data?.results || [];

      if (append) {
        setItems((prev) => [...prev, ...results]);
      } else {
        setItems(results);
      }
      setHasMore(Boolean(data?.next));
    } catch (err) {
      console.error("Failed to fetch items for minimum stock alert report:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(1);
      fetchItems(1, false);
    }, 400);

    return () => clearTimeout(handler);
  }, [filters]);

  const fetchMoreData = () => {
    if (hasMore && !loading) {
      const nextPage = page + 1;
      fetchItems(nextPage, true);
      setPage(nextPage);
    }
  };

  // Process rows with calculated stock values
  const processedItems = useMemo(() => {
    return items.map((item) => {
      const currentStock = Number(item.current_stock || 0);
      const reserveStock = Number(item.reserve_stock || 0);
      const freeStock = Math.max(0, currentStock - reserveStock);
      const minQty = Number(item.min_quantity || 0);
      const isBelowMin = minQty > 0 && freeStock < minQty;
      const isCritical = minQty > 0 && freeStock === 0;
      const shortage = isBelowMin ? minQty - freeStock : 0;

      return {
        ...item,
        currentStock,
        reserveStock,
        freeStock,
        minQty,
        isBelowMin,
        isCritical,
        shortage,
      };
    });
  }, [items]);

  // Filtered view based on "Show Only Below Min" switch
  const displayItems = useMemo(() => {
    if (!onlyBelowMin) return processedItems;
    return processedItems.filter((i) => i.isBelowMin);
  }, [processedItems, onlyBelowMin]);

  // KPI summary stats
  const totalMonitored = processedItems.length;
  const belowMinCount = processedItems.filter((i) => i.isBelowMin).length;
  const criticalCount = processedItems.filter((i) => i.isCritical).length;

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>
          Minimum Stock & Reorder Alert Report
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Monitors free stock (Current Stock − Reserve Stock) against minimum reorder level
        </Typography>
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}>
          <Paper
            elevation={0}
            sx={{
              p: 1.8,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              borderLeft: "4px solid #1e88e5",
              bgcolor: isDark ? alpha("#1e88e5", 0.08) : "#f0f7ff",
            }}
          >
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              TOTAL MONITORED ITEMS
            </Typography>
            <Typography variant="h4" fontWeight={700} color="#1e88e5" sx={{ mt: 0.5 }}>
              {totalMonitored}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Paper
            elevation={0}
            sx={{
              p: 1.8,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              borderLeft: "4px solid #fb8c00",
              bgcolor: isDark ? alpha("#fb8c00", 0.08) : "#fff8e1",
            }}
          >
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              ITEMS BELOW REORDER LEVEL
            </Typography>
            <Typography variant="h4" fontWeight={700} color="#fb8c00" sx={{ mt: 0.5 }}>
              {belowMinCount}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Paper
            elevation={0}
            sx={{
              p: 1.8,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              borderLeft: "4px solid #e53935",
              bgcolor: isDark ? alpha("#e53935", 0.08) : "#fde8e8",
            }}
          >
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              CRITICAL OUT OF FREE STOCK
            </Typography>
            <Typography variant="h4" fontWeight={700} color="#e53935" sx={{ mt: 0.5 }}>
              {criticalCount}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Filter Bar */}
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          mb: 2,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          display: "flex",
          flexWrap: "wrap",
          gap: 1.5,
          alignItems: "center",
        }}
      >
        <TextField
          size="small"
          placeholder="Filter by Item Code..."
          value={filters.item_code}
          onChange={(e) => setFilters((prev) => ({ ...prev, item_code: e.target.value }))}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 160, flex: 1 }}
        />

        <TextField
          size="small"
          placeholder="Filter by Item Name..."
          value={filters.name}
          onChange={(e) => setFilters((prev) => ({ ...prev, name: e.target.value }))}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 200, flex: 1.5 }}
        />

        <FormControlLabel
          control={
            <Switch
              checked={onlyBelowMin}
              onChange={(e) => setOnlyBelowMin(e.target.checked)}
              color="error"
            />
          }
          label={
            <Typography variant="body2" fontWeight={600} color={onlyBelowMin ? "error.main" : "text.secondary"}>
              Show Only Below Min Level ({belowMinCount})
            </Typography>
          }
          sx={{ ml: { xs: 0, sm: 1 } }}
        />
      </Paper>

      {/* Main Table */}
      <InfiniteScroll
        dataLength={displayItems.length}
        next={fetchMoreData}
        hasMore={hasMore}
        loader={
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        }
        scrollableTarget="min-stock-table-container"
      >
        <TableContainer
          component={Paper}
          id="min-stock-table-container"
          sx={{
            maxHeight: "68vh",
            overflowX: "auto",
            overflowY: "auto",
            width: "100%",
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
          }}
        >
          <Table
            stickyHeader
            size="small"
            sx={{
              minWidth: 1100,
              "& td, & th": {
                textAlign: "center",
                whiteSpace: "nowrap",
                borderBottom: `1px solid ${theme.palette.divider}`,
              },
            }}
          >
            <TableHead>
              <TableRow
                sx={{
                  "& th": {
                    fontWeight: 700,
                    fontSize: "0.75rem",
                    backgroundColor: isDark ? theme.palette.grey[900] : "#f5f5f5",
                    color: isDark ? theme.palette.primary.light : "#333",
                  },
                }}
              >
                <TableCell sx={{ minWidth: 50 }}>S. No.</TableCell>
                <TableCell align="left">Item Code</TableCell>
                <TableCell align="left">Item Name</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Unit</TableCell>
                <TableCell align="right">Min Reorder Level</TableCell>
                <TableCell align="right">Current Stock</TableCell>
                <TableCell align="right">Reserve Stock</TableCell>
                <TableCell align="right">Free Available Stock</TableCell>
                <TableCell align="right">Reorder Shortage</TableCell>
                <TableCell align="center">Stock Alert Status</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {displayItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 5 }}>
                    <Typography variant="body2" color="text.secondary">
                      {onlyBelowMin
                        ? "🎉 Great news! No items are currently below minimum reorder level."
                        : "No items found matching the filters."}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                displayItems.map((item, index) => {
                  const rowBg = item.isCritical
                    ? isDark
                      ? alpha("#e53935", 0.12)
                      : "#fff1f0"
                    : item.isBelowMin
                    ? isDark
                      ? alpha("#fb8c00", 0.1)
                      : "#fffbf0"
                    : index % 2 === 0
                    ? isDark
                      ? alpha(theme.palette.common.white, 0.02)
                      : "#fafafa"
                    : theme.palette.background.paper;

                  return (
                    <TableRow
                      key={item.id || index}
                      hover
                      sx={{
                        backgroundColor: rowBg,
                        borderLeft: item.isCritical
                          ? "4px solid #e53935"
                          : item.isBelowMin
                          ? "4px solid #fb8c00"
                          : "4px solid transparent",
                        "&:hover": {
                          backgroundColor: isDark
                            ? alpha(theme.palette.primary.main, 0.15)
                            : "#e3f2fd !important",
                        },
                        "& td": {
                          py: 1,
                          fontSize: { xs: "0.72rem", sm: "0.78rem" },
                          color: theme.palette.text.primary,
                        },
                      }}
                    >
                      <TableCell align="center">{index + 1}</TableCell>
                      <TableCell align="left" sx={{ fontWeight: 600 }}>
                        {item.item_code || "—"}
                      </TableCell>
                      <TableCell align="left" sx={{ maxWidth: 260 }}>
                        <Typography variant="body2" sx={{ fontSize: "0.78rem", fontWeight: 500 }}>
                          {item.name}
                        </Typography>
                      </TableCell>
                      <TableCell>{item.category_name || "—"}</TableCell>
                      <TableCell>{item.unit_name || item.unit?.symbol || "—"}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {item.minQty}
                      </TableCell>
                      <TableCell align="right">{item.currentStock}</TableCell>
                      <TableCell align="right">
                        {item.reserveStock > 0 ? (
                          <Chip
                            size="small"
                            label={item.reserveStock}
                            sx={{
                              bgcolor: alpha("#fb8c00", 0.15),
                              color: "#e65100",
                              fontWeight: 700,
                              fontSize: "0.72rem",
                              height: 20,
                            }}
                          />
                        ) : (
                          0
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          size="small"
                          label={item.freeStock}
                          sx={{
                            bgcolor: item.isBelowMin
                              ? alpha(theme.palette.error.main, 0.15)
                              : alpha(theme.palette.success.main, 0.12),
                            color: item.isBelowMin ? theme.palette.error.main : theme.palette.success.main,
                            fontWeight: 700,
                            fontSize: "0.75rem",
                            height: 22,
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        {item.shortage > 0 ? (
                          <Typography variant="body2" sx={{ color: "#e53935", fontWeight: 700, fontSize: "0.78rem" }}>
                            −{item.shortage}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {item.isCritical ? (
                          <Chip
                            icon={<ErrorOutlineIcon sx={{ fontSize: 13 }} />}
                            label="CRITICAL 0 FREE"
                            size="small"
                            sx={{
                              bgcolor: "#fde8e8",
                              color: "#e53935",
                              fontWeight: 700,
                              fontSize: "0.68rem",
                              border: "1px solid #e5393540",
                              "& .MuiChip-icon": { color: "#e53935" },
                            }}
                          />
                        ) : item.isBelowMin ? (
                          <Chip
                            icon={<WarningAmberIcon sx={{ fontSize: 13 }} />}
                            label="BELOW MIN"
                            size="small"
                            sx={{
                              bgcolor: "#fff8e1",
                              color: "#fb8c00",
                              fontWeight: 700,
                              fontSize: "0.68rem",
                              border: "1px solid #fb8c0040",
                              "& .MuiChip-icon": { color: "#fb8c00" },
                            }}
                          />
                        ) : (
                          <Chip
                            icon={<CheckCircleOutlineIcon sx={{ fontSize: 13 }} />}
                            label="ADEQUATE"
                            size="small"
                            sx={{
                              bgcolor: "#e8f5e9",
                              color: "#43a047",
                              fontWeight: 600,
                              fontSize: "0.68rem",
                              border: "1px solid #43a04740",
                              "& .MuiChip-icon": { color: "#43a047" },
                            }}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </InfiniteScroll>
    </Box>
  );
}

export default MinimumStockAlertReport;